// /supervision — the manager oversight view (orchestrator M4 §4.4). Every
// conversation currently handed off to a person: who holds it, how long it has
// waited, whether we owe a reply, and which SLA stages are breached. The manager
// never loses sight of a lead on the floor — no "the lead failed" without a
// verifiable trail — and can pull a stalled lead back to the queue.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  assignConversation,
  durationLabel,
  getHandoffOversight,
  type OversightItem,
  returnToPool,
} from './oversight-data.js'
import { useAssignableMembers } from './useAssignableMembers.js'
import {
  Button,
  Card,
  Chip,
  EmptyStateCard,
  FormField,
  Modal,
  PageHeader,
  Select,
  Spinner,
  toast,
} from '../../design-system/index.js'

const EyeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

/** Urgency tone for how long a conversation has waited. */
function waitTone(minutes: number): 'ok' | 'warn' | 'sens' {
  if (minutes >= 60) return 'sens'
  if (minutes >= 20) return 'warn'
  return 'ok'
}

/** The breached SLA stages as short Spanish labels (M4-L3). */
function slaBreachLabels(sla: OversightItem['sla']): string[] {
  const out: string[] = []
  if (sla.firstResponse) out.push('Sin respuesta')
  if (sla.idle) out.push('Inactivo')
  if (sla.resolution) out.push('Tiempo excedido')
  return out
}

export function OversightPage(): JSX.Element {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['oversight'],
    queryFn: getHandoffOversight,
    refetchInterval: 30_000,
  })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const returnToPoolMutation = useMutation({
    mutationFn: returnToPool,
    onSuccess: () => {
      toast.ok('Conversación devuelta a la cola.')
      void qc.invalidateQueries({ queryKey: ['oversight'] })
    },
    onError: () => toast.error('No se pudo devolver a la cola.'),
  })
  const [reassignId, setReassignId] = useState<string | null>(null)
  // Loaded up front, not on modal open: with only yourself on the list there is
  // nobody to reassign to, and the button must not render at all.
  const { canReassign } = useAssignableMembers()
  const assignMutation = useMutation({
    mutationFn: ({ id, agentId }: { id: string; agentId: string }) =>
      assignConversation(id, agentId),
    onSuccess: () => {
      toast.ok('Conversación reasignada.')
      setReassignId(null)
      void qc.invalidateQueries({ queryKey: ['oversight'] })
    },
    onError: () => toast.error('No se pudo reasignar.'),
  })

  if (isLoading) {
    return (
      <div data-testid="oversight" className="mx-auto w-full max-w-5xl py-10">
        <Spinner label="Cargando supervisión…" />
      </div>
    )
  }

  // NEVER show "todo bajo control" when the fetch failed — a waiting patient
  // could be invisible. Surface the error with a retry instead.
  if (isError || !data) {
    return (
      <div data-testid="oversight" className="mx-auto w-full max-w-5xl">
        <PageHeader
          title="Supervisión"
          subtitle="Conversaciones con una persona: quién las atiende y cuánto llevan esperando."
        />
        <div className="mt-6 flex flex-col items-start gap-3 rounded-xl border border-line bg-surface p-6">
          <p className="text-[13px] text-text-2">
            No pudimos cargar la supervisión. Esto no significa que no haya conversaciones
            esperando.
          </p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      </div>
    )
  }

  const items = data
  const pooled = items.filter((i) => i.inPool).length
  const held = items.length - pooled

  return (
    <div data-testid="oversight" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Supervisión"
        subtitle="Conversaciones con una persona: quién las atiende y cuánto llevan esperando."
      />
      {items.length === 0 ? (
        <div className="mt-6">
          <EmptyStateCard
            icon={EyeIcon}
            title="Todo bajo control"
            body="No hay conversaciones con una persona en este momento."
          />
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Chip tone="warn" dot>
              {pooled} en cola
            </Chip>
            <Chip tone="info" dot>
              {held} con un asesor
            </Chip>
          </div>
          <ul className="mt-4 space-y-2">
            {items.map((item) => (
              <li key={item.id}>
                <OversightRow
                  item={item}
                  onOpen={() => void navigate(`/bandeja?c=${encodeURIComponent(item.id)}`)}
                  onReturnToPool={() => returnToPoolMutation.mutate(item.id)}
                  onReassign={() => setReassignId(item.id)}
                  canReassign={canReassign}
                  returning={returnToPoolMutation.isPending}
                />
              </li>
            ))}
          </ul>
        </>
      )}
      {reassignId ? (
        <ReassignModal
          verb={assignVerb(items.find((i) => i.id === reassignId)?.holder ?? null)}
          onClose={() => setReassignId(null)}
          onConfirm={(agentId) => assignMutation.mutate({ id: reassignId, agentId })}
          busy={assignMutation.isPending}
        />
      ) : null}
    </div>
  )
}

/** CP-1 F-C: "Reasignar" presupone una asignación previa. Una conversación en
 *  la cola no la tiene: nadie la ha tomado todavía, así que la acción es
 *  asignarla, no reasignarla. Aquí la respuesta es exacta porque la fila SÍ
 *  sabe quién la tiene (`holder`). */
function assignVerb(holder: string | null): 'Asignar' | 'Reasignar' {
  return holder ? 'Reasignar' : 'Asignar'
}

function ReassignModal({
  verb,
  onClose,
  onConfirm,
  busy,
}: {
  verb: 'Asignar' | 'Reasignar'
  onClose: () => void
  onConfirm: (agentId: string) => void
  busy: boolean
}): JSX.Element {
  const { members, isLoading } = useAssignableMembers()
  const [agentId, setAgentId] = useState('')

  return (
    <Modal
      open
      onClose={onClose}
      title={`${verb} conversación`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => onConfirm(agentId)} loading={busy} disabled={!agentId}>
            {verb}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <FormField label="Asignar a" htmlFor="reassign-agent">
          <Select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            options={[
              { value: '', label: 'Elige una persona…' },
              ...members.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
        </FormField>
      )}
    </Modal>
  )
}

function OversightRow({
  item,
  onOpen,
  onReturnToPool,
  onReassign,
  canReassign,
  returning,
}: {
  item: OversightItem
  onOpen: () => void
  onReturnToPool: () => void
  onReassign: () => void
  canReassign: boolean
  returning: boolean
}): JSX.Element {
  return (
    <Card>
      <Card.Body>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={onOpen}
            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2 text-left"
          >
            <span
              aria-hidden
              className="grid size-9 flex-none place-items-center rounded-full bg-field text-[12px] font-bold text-text-2"
            >
              {item.contact.initials}
            </span>
            <div className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-bold text-text">
                {item.contact.name}
              </span>
              <span className="block truncate text-[11.5px] text-text-3">{item.contact.phone}</span>
            </div>
            <Chip tone={item.inPool ? 'warn' : 'info'} dot>
              {item.stateLabel}
            </Chip>
            <div className="flex flex-wrap gap-1">
              {slaBreachLabels(item.sla).map((label) => (
                <Chip key={label} tone="sens" dot>
                  SLA: {label}
                </Chip>
              ))}
            </div>
            <div className="w-28 text-[12px] text-text-2">
              {item.holder ? (
                <span className="truncate">{item.holder}</span>
              ) : (
                <span className="text-text-3">En cola</span>
              )}
            </div>
            <div className="w-24 text-right">
              <Chip tone={item.sla.any ? 'sens' : waitTone(item.waitingMinutes)}>
                {durationLabel(item.waitingMinutes)}
              </Chip>
              {item.waitingOnUs ? (
                <span className="mt-0.5 block text-[10px] font-semibold text-sens">
                  Te toca responder
                </span>
              ) : null}
            </div>
          </button>
          {/* C5: the whole row already opens the thread, but an unlabeled
              row-click is invisible — name the action. */}
          <Button variant="secondary" size="sm" onClick={onOpen}>
            Abrir en Bandeja
          </Button>
          {/* Nobody else on the team ⇒ no reassign. A lone Dueño reassigning to
              herself is ceremony with no destination. */}
          {canReassign ? (
            <Button variant="secondary" size="sm" onClick={onReassign}>
              {assignVerb(item.holder)}
            </Button>
          ) : null}
          {!item.inPool ? (
            <Button variant="secondary" size="sm" onClick={onReturnToPool} disabled={returning}>
              Devolver a la cola
            </Button>
          ) : null}
        </div>
      </Card.Body>
    </Card>
  )
}
